import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, Tag, Calendar, ChevronRight } from 'lucide-react';
import { getBlogBySlug, getRelatedBlogs } from '../data/blogData';
import ContentRenderer from './ContentRenderer';
import Navbar from './Navbar';
import Footer from './Footer';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (post) {
      document.title = `${post.title} | MedCertGen Blog`;
    }
  }, [slug, post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = getRelatedBlogs(slug);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="blog-post-article">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition mb-8" data-testid="blog-back-link">
          <ArrowLeft size={16} /> Back to all articles
        </Link>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-5">
          <Tag size={12} /> {post.category}
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-gray-900" data-testid="blog-post-title">
          {post.title}
        </h1>

        <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-gray-500 pb-6 border-b border-gray-200">
          <span className="flex items-center gap-1.5"><Clock size={14} /> {post.readTime}</span>
          <span className="flex items-center gap-1.5"><Calendar size={14} /> Last updated {post.updated}</span>
          <span className="text-gray-300">·</span>
          <span>By the MedCertGen editorial team</span>
        </div>

        <p className="mt-8 text-lg text-gray-700 leading-relaxed font-medium">{post.excerpt}</p>

        <div className="mt-6">
          <ContentRenderer content={post.content} />
        </div>

        {/* Tags */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-wrap gap-2" data-testid="blog-post-tags">
            {post.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-teal-50 border border-blue-100">
          <h3 className="text-xl font-semibold text-gray-900">Need a sample certificate for testing or demos?</h3>
          <p className="mt-2 text-sm text-gray-600">Generate a realistic Indian medical certificate in 60 seconds — completely free, no signup needed. Every certificate is clearly marked for educational use only.</p>
          <Link to="/" data-testid="blog-cta-generator" className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition">
            Try the free generator <ChevronRight size={16} />
          </Link>
        </div>
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="border-t border-gray-200 bg-gray-50 py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Related articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="related-articles">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`related-blog-${r.slug}`} className="block p-5 rounded-xl bg-white border border-gray-200 hover:border-blue-400 hover:shadow transition">
                  <div className="text-xs text-blue-700 font-medium mb-2">{r.category}</div>
                  <h3 className="font-semibold text-gray-900 text-base leading-snug">{r.title}</h3>
                  <div className="mt-3 text-xs text-gray-500 flex items-center gap-1.5"><Clock size={12} /> {r.readTime}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPost;
